import React, { useState } from 'react';
import { AxiosResponse } from 'axios';
import { useHistory } from 'react-router-dom';

import { MemberSignupResponse, getPartialMember, useSignupUser } from '../../hooks/api/useUser';
import { emailRegex } from '../../utils/constants';
import rollbar from '../../utils/rollbar';
import { LoginButton } from '../../components/login-button';
import { OnboardingLayout } from '../onboarding/common/layout';
import { ROUTE_PATH } from '../../routes/route-paths';

/**
 * Validates an email address.
 * @param email - The email address to validate.
 * @returns False if the email is invalid, true if the email is valid.
 */
const validateEmail = (email: string) => !(email === '' || email === undefined || !emailRegex.test(email));

const SignupPage: React.FC = () => {
  const signupUser = useSignupUser();
  const navigate = useHistory();

  const [emailAddress, setEmailAddress] = useState('');
  const [emailError, setEmailError] = useState<{ msg: string; disableInput: boolean } | null>(null);
  const [inProgress, setInProgress] = useState<boolean>(false);

  const handleSetEmail = (email: string) => {
    const trimmedEmail = email.trim();
    setEmailAddress(trimmedEmail);

    // Clear the error if the email becomes valid
    if (validateEmail(trimmedEmail)) {
      setEmailError(null);
    }
  };

  const signup = (clientCode: string) =>
    signupUser.mutateAsync(
      { email_address: emailAddress, package_code: clientCode },
      {
        onSuccess: (userResponse: AxiosResponse<MemberSignupResponse>) => {
          sessionStorage.setItem('partial_member_id', JSON.stringify(userResponse.data.id));
          navigate.push({
            pathname: ROUTE_PATH.SIGNUP_EMAIL_VERIFICATION,
            search: `?email_address=${encodeURIComponent(emailAddress)}&mode=welcome`,
          });
        },
        onError: (error: any) => {
          setEmailError({ msg: error.response.data.description, disableInput: false });
        },
      },
    );

  const handlePartialMemberError = (err: any) => {
    switch (err.response?.status) {
      case 400:
        let error_message = err.response?.data.description;
        error_message = error_message.charAt(0).toUpperCase() + error_message.slice(1);
        setEmailError({ msg: error_message, disableInput: false });
        break;
      case 404:
        // Redirect to group verification page
        navigate.push({
          pathname: ROUTE_PATH.SIGNUP_GROUP_VERIFICATION,
          search: `?email_address=${encodeURIComponent(emailAddress)}`,
        });
        return; // Return early to prevent further execution
      default:
        setEmailError({
          msg: 'Something went wrong; please try again later.',
          disableInput: true,
        });
        rollbar.error(`Error looking for a partial member`, new Date(), {
          message: `Environment: ${process.env.REACT_APP_ENVTYPE}; Error: ${err}`,
        });
    }
  };

  const handleContinue = async () => {
    if (!validateEmail(emailAddress)) {
      setEmailError({ msg: 'Enter valid email address', disableInput: true });
      return;
    } else {
      setEmailError(null);
    }

    setInProgress(true);

    getPartialMember(
      { email_address: emailAddress },
      // onSuccess:
      (partialMember) => {
        // Yes - there already exists a partial member.  IE, they came in from an eligibility file.
        // then redirect to Verification page
        sessionStorage.setItem('partial_member_id', JSON.stringify(partialMember.user_id));
        navigate.push({
          pathname: ROUTE_PATH.SIGNUP_EMAIL_VERIFICATION,
          search: `?email_address=${encodeURIComponent(emailAddress)}&mode=welcome`,
        });
      },
      (err) => {
        const clientCode = sessionStorage.getItem('clientCode');
        if (clientCode) {
          signup(clientCode).catch(() => {
            handlePartialMemberError(err);
          });
        } else {
          handlePartialMemberError(err);
        }
      },
      () => {
        setInProgress(false);
      },
    );
  };

  return (
    <OnboardingLayout>
      <div className="mb-6 space-y-1">
        <h1 className="font-albert-sans text-xl font-semibold leading-[1.5] text-black">Create your account</h1>
        <p className="font-open-sans text-sm font-normal leading-[1.5] text-black">
          If registered through an organization, use your email associated with your organization.
        </p>
      </div>

      <div className="mb-6 flex flex-col gap-4">
        <div className="flex flex-col gap-2.5">
          <div className="flex items-center gap-1">
            <label className="font-albert-sans text-label-gray text-sm font-medium leading-5">Email address</label>
            <span className="text-error">*</span>
          </div>
          <div className="relative">
            <div className="absolute left-3 top-1/2 -translate-y-1/2">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M2.66667 2.66667H13.3333C14.0667 2.66667 14.6667 3.26667 14.6667 4V12C14.6667 12.7333 14.0667 13.3333 13.3333 13.3333H2.66667C1.93333 13.3333 1.33333 12.7333 1.33333 12V4C1.33333 3.26667 1.93333 2.66667 2.66667 2.66667Z"
                  stroke="#6a7282"
                  strokeWidth="1.33333"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M14.6667 4L8 8.66667L1.33333 4"
                  stroke="#6a7282"
                  strokeWidth="1.33333"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <input
              required
              id="email_address"
              type="email"
              value={emailAddress}
              autoComplete="email"
              placeholder="Enter your email address"
              onChange={(e) => {
                handleSetEmail(e.target.value);
              }}
              disabled={emailError?.disableInput}
              className={`w-full rounded-xl border ${
                emailError?.msg ? 'border-error' : 'border-input-border-gray'
              } font-open-sans focus:ring-button-primary placeholder:text-placeholder-gray bg-gray-50 py-[10px] pl-10 pr-3 text-sm leading-5 text-black focus:border-transparent focus:outline-none focus:ring-2 disabled:cursor-not-allowed disabled:opacity-50`}
            />
          </div>
          {emailError?.msg && <p className="font-open-sans text-error text-sm leading-5">{emailError.msg}</p>}
        </div>
      </div>

      <div className="mb-6 flex flex-col gap-3">
        <button
          data-id="continue-button"
          className="font-albert-sans bg-button-primary hover:bg-button-primary-hover w-full rounded-full px-7 py-[10px] text-base font-medium leading-5 text-white shadow-[0px_1px_0.5px_0.05px_rgba(29,41,61,0.02)] transition-colors disabled:cursor-not-allowed disabled:opacity-50"
          onClick={handleContinue}
          disabled={emailError?.disableInput || inProgress}
        >
          {inProgress ? 'Processing...' : 'Continue'}
        </button>
      </div>

      <div className="flex items-center justify-center">
        <LoginButton />
      </div>
    </OnboardingLayout>
  );
};

export default SignupPage;
